/**
 * Session Storage Helpers
 * 
 * Utilities for persisting booking state using browserAdapter.session API.
 * Uses namespaced keys to avoid conflicts with other app data.
 * 
 * **Important**: Uses browserAdapter.session for iframe-safe sessionStorage.
 * Falls back gracefully if storage is unavailable (parent bridge → memory).
 * 
 * @module booking/context/sessionStorage
 */

import { browserAdapter } from '~/services/browserAdapter';

/** Namespace for all booking-related storage keys */
const STORAGE_PREFIX = 'web-portal-2:booking';

/**
 * Generate a namespaced storage key
 * 
 * @param key - The key name (e.g., 'selectedDate')
 * @returns Namespaced key (e.g., 'web-portal-2:booking:selectedDate')
 */
function getStorageKey(key: string): string {
  return `${STORAGE_PREFIX}:${key}`;
}

/**
 * Save data to session storage
 * 
 * Automatically serializes to JSON.
 * Uses browserAdapter for iframe-safe storage.
 * Fails silently if storage is unavailable or quota exceeded.
 * 
 * @param key - Storage key (will be namespaced)
 * @param value - Value to store (any JSON-serializable data)
 * 
 * @example
 * ```typescript
 * await saveToSession('selectedDate', new Date().toISOString());
 * await saveToSession('selectedBay', { id: 1, name: 'Bay 1' });
 * ```
 */
export async function saveToSession<T>(key: string, value: T): Promise<void> {
  try {
    const storageKey = getStorageKey(key);
    const serialized = JSON.stringify(value);
    await browserAdapter.session.set(storageKey, serialized);
  } catch (error) {
    // Fail silently - storage may be disabled or quota exceeded
    if (import.meta.env.DEV) {
      console.warn(`[BookingContext] Failed to save to storage: ${key}`, error);
    }
  }
}

/**
 * Load data from session storage
 * 
 * Automatically deserializes from JSON.
 * Uses browserAdapter for iframe-safe storage.
 * Returns null if key doesn't exist or data is invalid.
 * 
 * @param key - Storage key (will be namespaced)
 * @returns Parsed value or null
 * 
 * @example
 * ```typescript
 * const date = await loadFromSession<string>('selectedDate');
 * const bay = await loadFromSession<Bay>('selectedBay');
 * ```
 */
export async function loadFromSession<T>(key: string): Promise<T | null> {
  try {
    const storageKey = getStorageKey(key);
    const serialized = await browserAdapter.session.get(storageKey);
    
    if (!serialized) {
      return null;
    }
    
    return JSON.parse(serialized) as T;
  } catch (error) {
    // Fail silently - storage may be disabled or data is corrupted
    if (import.meta.env.DEV) {
      console.warn(`[BookingContext] Failed to load from storage: ${key}`, error);
    }
    return null;
  }
}

/**
 * Remove item from session storage
 * 
 * Uses browserAdapter for iframe-safe storage.
 * 
 * @param key - Storage key (will be namespaced)
 * 
 * @example
 * ```typescript
 * await clearFromSession('selectedBay');
 * ```
 */
export async function clearFromSession(key: string): Promise<void> {
  try {
    const storageKey = getStorageKey(key);
    await browserAdapter.session.remove(storageKey);
  } catch (error) {
    // Fail silently
    if (import.meta.env.DEV) {
      console.warn(`[BookingContext] Failed to clear from storage: ${key}`, error);
    }
  }
}

/**
 * Clear all booking-related data from session storage
 * 
 * Removes all keys with the booking namespace prefix.
 * Uses browserAdapter for iframe-safe storage.
 * 
 * @example
 * ```typescript
 * await clearAllBookingData();
 * ```
 */
export async function clearAllBookingData(): Promise<void> {
  try {
    // Get all keys from session storage (not localStorage)
    const allKeys = await browserAdapter.session.keys();
    
    // Filter keys with our prefix
    const keysToRemove = allKeys.filter(key => key.startsWith(STORAGE_PREFIX));
    
    // Remove all matching keys
    await Promise.all(
      keysToRemove.map(key => browserAdapter.session.remove(key))
    );
    
    if (import.meta.env.DEV) {
      console.log(`[BookingContext] Cleared ${keysToRemove.length} booking items from storage`);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[BookingContext] Failed to clear booking data from storage', error);
    }
  }
}
